<template>
  <div>
    <div class="m-0" v-if="isEvasiveManeuverRequest">
      <div class="text-xs-center" v-if="displayComputationWidget">
        <exo-mission-computation></exo-mission-computation>
      </div>
      <div v-if="displayError" class="text-center">
        <v-dialog v-model="dialog">
          <template v-slot:activator="{ on: dialog }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-col class="bug-btn-col">
                  <v-btn
                    large
                    class="bug-button"
                    v-on="{ ...dialog, ...tooltip }"
                  >
                    <icon class="red--text" scale="1.5" name="user-injured" />
                    <span class="red--text bug-btn-txt">Bug report</span>
                  </v-btn>
                </v-col>
              </template>
              <span>Display bug report</span>
            </v-tooltip>
          </template>
          <error-log-display
            :root="_self"
            :computationLog="currentResultsLog"
          ></error-log-display>
        </v-dialog>
      </div>
      <div v-if="displayResult & !displayError" ref="dashboard">
        <v-row>
          <v-col class="pb-3">
            <v-row no-gutters>
              <div class="d-inline-flex ml-3 justify-center align-center">
                <span class="headline grey--text">{{
                  currentMission.name.toUpperCase()
                }}</span>
              </div>
              <v-spacer></v-spacer>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="mt-0"></v-divider>
        <div class="px-3">
          <v-row>
            <v-col>
              <div>
                <v-row class="px-5 mt-2">
                  <span class="text-h6 text-truncate"
                    >Evasive Maneuver Scenarios </span
                  ><exo-help-dialog
                    margin="mt-1"
                    title="Evasive Maneuver Scenarios"
                  >
                    The designed evasive scenarios are summarized and can be
                    added to the maneuver comparator.
                  </exo-help-dialog>
                </v-row>
              </div>
            </v-col>
            <v-col align="right"
              ><ComponentExporter
                :elementToExport="$refs.dashboard"
                :buttonName="'Export this dashboard ...'"
                size="medium"
            /></v-col>
          </v-row>
          <v-row>
            <v-col>
              <EvasiveManeuverTable
                :computeServiceRecovery="computeServiceRecovery"
                :showCustomScenarios="associatedCustomEvasiveReports.length > 0"
                :evasiveManeuverReportsWithId="evasiveManeuverReportsWithId"
                :fieldsToDisplay="fieldsToDisplay"
                v-on:add-maneuver="addManeuverToComparator($event)"
                v-on:remove-maneuver="removeManeuverFromComparator($event)"
                v-on:export-maneuver="exportManeuver(getManeuverById($event))"
              />
            </v-col>
            <v-col
              ><EvasiveManeuversParetoFront
                :maneuvers="evasiveManeuversForPareto"
                :fieldsToCompare="fieldsToDisplay"
                :comparedManeuvers="comparedManeuvers"
              ></EvasiveManeuversParetoFront
            ></v-col>
          </v-row>
        </div>
        <div class="px-3 pt-3">
          <EvasiveManeuverComparator
            :comparedManeuversReports="comparedManeuvers"
            :computeServiceRecovery="computeServiceRecovery"
            :computeChaserReaction="computeChaserReaction"
            :referenceChaserToTargetRelativeDistanceResults="
              currentResultsLog.results
                .referenceChaserToTargetRelativeDistanceResults
            "
            v-on:remove-maneuver="removeManeuverFromComparator($event)"
            v-on:export-maneuver="exportManeuver(getManeuverById($event))"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import resultsDescription from '../../tools/resultsDescription.json'
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import { mapGetters, mapActions } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import { setTimeout } from 'timers'
import dashboardTools from '../../tools/dashboard'
import DataPanel from '../Defense/DataPanel'
import moment from 'moment'
import Plotly from 'plotly.js-dist'
import EvasiveManeuversParetoFront from './EvasiveManeuversParetoFront'
import EvasiveManeuverTable from './EvasiveManeuverTable'
import EvasiveManeuverComparator from './EvasiveManeuverComparator'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { elementToSVG, inlineResources } from 'dom-to-svg'
import svgStyle from './svgStyle'
import ComponentExporter from './ComponentExporter'

Vue.use(dashboardComponents)

export default {
  name: 'EvasiveManeuverDashboard',
  mixins: [errorMixin, plotLayout, plotInput, plotData],
  components: {
    DataPanel,
    EvasiveManeuversParetoFront,
    EvasiveManeuverTable,
    EvasiveManeuverComparator,
    ComponentExporter
  },
  data() {
    return {
      dialog: false,
      type: 'ReactionManeuverReport',
      exporting: false
    }
  },

  watch: {
    currentResultsLog: {
      handler() {
        this.refreshCustomEvasiveScenarios()
      },
      immediate: true
    },
    allComparedEvasiveManeuversIds(v) {
      if (v && Object.keys(v) > 0) {
        this.updateLocallySavedParameters()
      }
    }
  },

  mounted() {
    this.restoreLocallySavedComparedEvasiveManeuversIds()
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog',
      allComparedEvasiveManeuversIds: 'allComparedEvasiveManeuversIds',
      customEvasiveReports: 'customEvasiveReports',
      customEvasiveScenarios: 'customEvasiveScenarios'
    }),
    evasiveManeuversForPareto() {
      return this.evasiveManeuverReportsWithId.map((reports) => {
        return {
          ...reports.targetReactionManeuverReport,
          id: reports.id
        }
      })
    },

    fieldsToDisplay() {
      if (this.computeServiceRecovery) {
        return [
          'targetTotalDeltaV',
          'targetServiceLossDuration',
          'closestApproachDistance'
        ]
      } else if (this.currentMission.evasiveType === 'RPO_ESCAPE') {
        return [
          'targetTotalDeltaV',
          'targetReactionDuration',
          'upwardThresholdCrossingDate'
        ]
      } else {
        return [
          'targetTotalDeltaV',
          'targetReactionDuration',
          'closestApproachDistance'
        ]
      }
    },

    computeChaserReaction() {
      return this.currentMission?.computeChaserReaction
    },
    computeServiceRecovery() {
      return this.currentMission?.computeServiceRecovery
    },
    displayComputationWidget() {
      return this.computationNeeded || !this.resultsLoaded
    },
    displayResult() {
      return this.resultsLoaded && !this.computationNeeded
    },
    displayError() {
      return dashboardTools.displayError(this)
    },
    computationNeeded() {
      return (
        this.currentMission &&
        this.currentMission.computationRequested &&
        this.currentMission.computationLogId === ''
      )
    },

    resultsLoaded() {
      return (
        this.currentResultsLog != null && this.currentResultsLog.results != null
      )
    },
    resultAvailable() {
      return (
        this.resultsLoaded &&
        this.currentResultsLog.results ===
          Object(this.currentResultsLog.results)
      ) // check object, not array
    },
    computationLogId() {
      return this?.currentResultsLog?.id
    },
    isEvasiveManeuverRequest() {
      return this?.currentMission?.typeName === 'EvasiveManeuverRequest'
    },
    evasiveManeuverReports() {
      return this?.currentResultsLog?.results?.evasiveManeuverReports
    },
    associatedCustomEvasiveReports() {
      const customEvasiveScenariosNames = this.customEvasiveScenarios
        .filter((scenario) => scenario.missionId === this.currentMission.id)
        .map((scenario) => scenario.name)
      return this.customEvasiveReports
        .filter((reports) => customEvasiveScenariosNames.includes(reports.id))
        .map((m) => {
          return { ...m, custom: true }
        })
    },
    evasiveManeuverReportsWithId() {
      if (this.evasiveManeuverReports != null) {
        const all = [
          ...this.associatedCustomEvasiveReports,
          ...this.evasiveManeuverReports.map((m, i) => {
            return { ...m, id: (i + 1).toString(), custom: false }
          })
        ]
        return all
      } else {
        return []
      }
    },

    comparedManeuversStoreId() {
      return this?.currentResultsLog?.id + this?.currentResultsLog?.updatedAt
    },
    comparedManeuvers() {
      const comparedIds =
        this?.allComparedEvasiveManeuversIds?.[this.comparedManeuversStoreId] ||
        new Set()
      return this.evasiveManeuverReportsWithId.filter((maneuverReports) =>
        comparedIds.has(maneuverReports.id)
      )
    }
  },

  methods: {
    ...mapActions([
      'addToComparedEvasiveManeuversIds',
      'deleteFromComparedEvasiveManeuversIds',
      'refreshCustomEvasiveScenarios',
      'restoreLocallySavedComparedEvasiveManeuversIds'
    ]),
    removeManeuverFromComparator(maneuverToRemoveId) {
      this.deleteFromComparedEvasiveManeuversIds({
        id: maneuverToRemoveId,
        key: this.comparedManeuversStoreId
      })
      return maneuverToRemoveId
    },

    addManeuverToComparator(maneuverToAddId) {
      this.addToComparedEvasiveManeuversIds({
        id: maneuverToAddId,
        key: this.comparedManeuversStoreId
      })
      return maneuverToAddId
    },

    getDisplayValue(context, fieldName, data) {
      const field = resultsDescription[context][fieldName]
      return dataTools.convertSi2Display(field.type, data[fieldName])
    },
    getDisplayString(context, fieldName, data) {
      const field = resultsDescription[context][fieldName]
      const fieldUnit = dataTools.getDefaultGuiUnit(field.type)
      const label = fieldUnit ? `${field.label} (${fieldUnit})` : field.label
      const value = dataTools.convertSi2gui(field.type, data[fieldName])
      return `${label}: ${value}`
    },

    getManeuverById(maneuverId) {
      return this.evasiveManeuverReportsWithId.find(
        (reports) => reports.id === maneuverId
      )?.targetReactionManeuverReport
    },

    exportManeuver(reactionManeuverReport) {
      let text = ''
      const title = `Evasive scenario ${reactionManeuverReport.targetImpulseManeuverReports?.['0']?.dateOfManeuver}`
      const line =
        '-----------------------------------------------------------------\n'

      text += line
      text += 'Evasive Maneuver performance\n'
      text += line
      for (const fieldName of [
        'targetReactionDuration',
        'targetTotalDeltaV',
        'closestApproachDistance'
      ]) {
        text +=
          this.getDisplayString(
            'ReactionManeuverReport',
            fieldName,
            reactionManeuverReport
          ) + '\n'
      }

      reactionManeuverReport.targetImpulseManeuverReports.forEach(
        (report, index) => {
          text += line
          text += `Impulsive maneuver n°${index + 1} details\n`
          text += line
          for (const [field, value] of Object.entries(report)) {
            text +=
              this.getDisplayString('ImpulseManeuverReport', field, report) +
              '\n'
          }
        }
      )
      dataTools.downloadFile(title, text, 'txt')
    },

    getUnitLabelFromPath(fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      return dataTools.getUnitLabel(dataTools.getDefaultGuiUnit(fieldType))
    },
    getLabelFromPath(fieldPath, context) {
      return dataTools.getFieldLabelFromPath(fieldPath, context)
    },
    getDisplayValueFromPath(value, fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      return dataTools.convertSi2gui(fieldType, value)
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');

.dialogContentContainer {
  padding: 30px;
  height: calc(100vh - 70px);
}

#fullScreenPlotDiv {
  width: 100%;
  height: 100%;
}
</style>
